'use client';

import {motion} from 'framer-motion';
import styles from './styles.module.scss';
import {useEffect, useMemo, useState} from 'react';
import {
  handleLogin,
  handleUserInfo,
  inAppStore,
  loginState,
} from '@/utils/event/util';
import globalDomains from '@/utils/global-domains';
import MarketingConsentEventApi from '@/api/events/marketing-consent';
import {useSearchParams} from 'react-router-dom';
import LogApi from '@/api/common/log';
import {
  getCookie,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from '@/utils/main';

const EVENT_END_DATE = new Date('2025-08-23 00:00:00');

const INACTIVE_BUTTON_TEXT = {
  '-104': '이벤트 참여 불가', // 해외 유저
  '-110': '이벤트 참여 불가', // 1차 이벤트 참여한 유저
  '-111': '이벤트 참여 완료',
};

const LAYER_MESSAGE = {
  1: '이벤트 참여가 완료되었습니다.',
  '-101': '로그인 후\n이용 가능합니다.',
  '-102': '이벤트가\n종료되었습니다.',
  // '-103': '조회된 정보 없음',
  '-104': '해외에서 가입한 계정은\n이벤트 참여를 하실 수 없습니다.',
  // '-108': '유효하지 않은 요청',
  '-110': '이미 1차 마케팅 수신 동의\n이벤트에 참여하셨습니다.',
  '-111': '이미 이벤트에 참여하셨습니다.',
};

const MARKETING_CONSENT_NON_TARGET = 'marketing_consent_non_target';

const MarketingConsent = ({onBodyId}) => {
  const [loading, setLoading] = useState(false);
  /** 이벤트 참여 여부 */
  const [buttonStatus, setButtonStatus] = useState(null);
  const [layerStatus, setLayerStatus] = useState(null);

  /** 로그인 한 사용자의 정보 */
  const {userId, userNickname} = useMemo(() => handleUserInfo(), []);
  /** 이벤트 기간 여부 */
  const isEventActive = useMemo(() => new Date() < EVENT_END_DATE, []);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const checkAccess = () => {
      if (typeof window.afreeca !== 'undefined') {
        const agentCookie = getCookie('agent') || '';
        const decodedAgentCookie = decodeURIComponent(
          agentCookie.replace(/\+/g, ' '),
        );
        const isPlaystore = decodedAgentCookie.includes('isPlayStore=Y');
        const store = inAppStore();
        if (store !== 'P' && !isPlaystore) {
          alert('해당 이벤트는 구글 플레이스토어 유저만 참여할 수 있습니다.');
          if (window.afreeca) {
            // pc 로 접근 할떄는 메인 페이지로
            if (!window.afreeca.link.isMobile()) {
              window.location.href = globalDomains.WWW_AFREECATV;
            } else {
              // 모바일일때
              if (window.afreeca.link.isWebview()) {
                // 웹뷰는 스킴 close
                window.afreeca.link.close();
              } else {
                // 웹뷰 아닐때는 window close
                window.location.href = globalDomains.M_AFREECATV;
              }
            }
          } else {
            window.location.href = globalDomains.WWW_AFREECATV;
          }
          return;
        }
        /**
         * 초기 로드시 body id 셋팅
         */
        onBodyId && onBodyId(styles.body);
        setLoading(true);

        // 진입 경로
        LogApi.sendClickLog({
          codeType: 'mkt_consent',
          sendData: {
            event_path: searchParams.get('utm_source'),
          },
        });
      } else {
        // window.afreeca가 로드될 때까지 재시도
        setTimeout(checkAccess, 100);
      }
    };

    checkAccess();
  }, [onBodyId]);

  /** 화면 최초 렌더링 시 동작 */
  // 첫 접근 시 버튼 텍스트는 마케팅 수신 동의하기 | 이벤트 참여 완료
  // 이벤트 참여 불가한 유저의 경우 버튼 클릭하여 사유를 얼럿으로 확인하면 버튼이 이벤트 참여 불가로 변경됨
  // 이벤트 참여 불가 유저가 페이지 재 접근 시 버튼 텍스트가 이벤트 참여 불가로 노출되어야 함
  useEffect(() => {
    const handleUserEventInfo = async () => {
      if (!userId) {
        return;
      }

      try {
        const {result} = await MarketingConsentEventApi.marketingConsentInfo();

        // 1 참여하기
        // -101 비로그인
        // -104 해외 유저 참여불가
        // -110 1차 이벤트 참여 유저 - 참여 불가
        // -111 2차 이벤트 참여 완료
        const status = Number(result);

        if (status === -111) {
          setButtonStatus(status);
        } else {
          const nonTarget = JSON.parse(
            getLocalStorage(MARKETING_CONSENT_NON_TARGET, null),
          );
          if (nonTarget) {
            if (nonTarget.userId === userId && nonTarget.status === status) {
              setButtonStatus(status);
            } else {
              removeLocalStorage(MARKETING_CONSENT_NON_TARGET);
            }
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    handleUserEventInfo();
  }, [userId]);

  /**
   * 웹뷰로 접근했을 때 뒤로가기 동작 시 그냥 히스토리 상관 없이 바로 닫히도록 설정
   * Note. 이 코드는 웹뷰에서 포커스가 최소 1회 발동한 다음에만 정상 동작합니다.
   * 웹 브라우저의 설계상 정책으로 웹 페이지가 기기의 기본 뒤로가기 동작을 조작해 사용자의 뒤로가기 시 URL을 하이잭킹 하는 것을 막기 때문.
   * Ref. https://stackoverflow.com/questions/57339098/chrome-popstate-not-firing-on-back-button-if-no-user-interaction
   */
  useEffect(() => {
    window.history.replaceState({}, '', window.location.href);
    history.pushState(null, '', window.location.href);

    const handleOnPopState = () => {
      if (window.afreeca?.link?.isWebview()) {
        window.afreeca?.link?.closeWebview();
      }
    };

    window.addEventListener('popstate', handleOnPopState);

    return () => {
      window.removeEventListener('popstate', handleOnPopState);
    };
  }, []);

  // 이벤트 참여하기
  const handleJoinEvent = async () => {
    if (!userId) {
      handleLogin();
      return;
    }

    if (!isEventActive) {
      setLayerStatus(-102);
      return;
    }

    try {
      const {result} = await MarketingConsentEventApi.joinEvent();
      const status = Number(result);

      setLayerStatus(status);

      if (status === -104 || status === -110) {
        setLocalStorage(
          MARKETING_CONSENT_NON_TARGET,
          JSON.stringify({userId, status}),
        );
        return;
      }

      if (status === 1) {
        MarketingConsentEventApi.sendAlimtalk();
        return;
      }
    } catch (error) {
      setLayerStatus(-108);
    }
  };

  const closeLayer = () => {
    setLayerStatus(null);
    window.location.reload();
  };

  return (
    loading && (
      <div id={styles.wrap}>
        <header className={styles.header}>
          <div className={styles.inn}>
            <h1>
              <a
                href='#'
                onClick={() => {
                  /**
                   * 웹뷰 -> home 이동 스킴이 없어 리로드로 적용
                   */
                  if (window.afreeca.link.isWebview()) {
                    window.location.reload();
                    return;
                  }

                  window.location.href = `${globalDomains.WWW_AFREECATV}`;
                  return;
                }}>
                SOOP
              </a>
            </h1>
            <div className={styles.login_wrap}>
              <button
                type='button'
                className={loginState ? styles.logout : styles.login}
                onClick={handleLogin}>
                {loginState ? '로그아웃' : '로그인'}
              </button>
            </div>
          </div>
        </header>

        <div className={styles.visual}>
          <div className={styles.inn}>
            <motion.h2
              className={styles.visual_title}
              initial={{opacity: 0, y: -50}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{
                type: 'spring',
                bounce: 0.45,
                ease: 'easeInOut',
                duration: 1,
                delay: 0.8,
              }}>
              <p>가볍게 동의하고 CU 상품권 받자!</p>
            </motion.h2>
            <motion.span
              className={styles.event_period}
              initial={{opacity: 0, y: -50}}
              whileInView={{opacity: 1, y: 0}}
              viewport={{once: true}}
              transition={{
                type: 'spring',
                bounce: 0.45,
                ease: 'easeInOut',
                duration: 1,
                delay: 0.8,
              }}>
              25. 07. 24(목) ~ 25. 08. 22(금)
            </motion.span>
            <div className={styles.visual_motion}>
              <span>CU 모바일 금액권 3,000원</span>
            </div>
            <p className={styles.item_desc}>
              <em className={styles.num}>1,000명</em>에게 쏜다! 마케팅 수신 동의
              하고
              <br />
              <em className={styles.item}>CU 상품권</em> 받아가세요!
            </p>
            <motion.i
              className={styles.ele_1}
              animate={{
                y: [-10, 10, -10],
              }}
              transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              }}></motion.i>
            <motion.i
              className={styles.ele_2}
              animate={{
                y: [-10, 10, -10],
              }}
              transition={{
                repeat: Infinity,
                duration: 2,
                ease: 'easeInOut',
              }}></motion.i>
          </div>
        </div>
        <div className={styles.contents}>
          <div className={styles.inn}>
            <motion.h2>
              <span>마케팅 수신 동의 한 번으로</span>
              <p>상품권과 서비스 꿀팁이 한가득!!</p>
            </motion.h2>
            <div className={styles.gift_wrap}>
              <div className={styles.gift}>
                <img
                  src={`${globalDomains.RES_AFREECATV}/eventzone/event/2025/marketing_consent/img/gift_item.png`}
                  alt='CU 모바일 금액권 3,000원'
                />
                <span className={styles.sticker}>추첨 1,000명</span>
              </div>
              <span className={styles.gift_name}>
                [CU] 편의점 모바일 상품권 3천 원권
              </span>
            </div>

            <div className={styles.event_detail}>
              <dl>
                <dt>이벤트 참여 기간</dt>
                <dd>25. 07. 24(목) ~ 25. 08. 22(금)</dd>
              </dl>
              <dl>
                <dt>당첨자 발표</dt>
                <dd>25. 08. 28일(목)</dd>
              </dl>
            </div>

            <p className={styles.noti_msg}>
              마케팅 수신 동의 시, 이메일, 문자, 야간 카카오톡 수신에
              <br />
              모두 동의됩니다.
            </p>
          </div>
        </div>
        <div className={styles.notice_list}>
          <div className={styles.inn}>
            <strong className={styles.title}>
              이벤트 <em>유의사항</em>
            </strong>
            <ul>
              <li>
                본 이벤트는 Android 구글 플레이 스토어 다운로드 고객 중 광고성
                수신 여부 (비동의)한 고객 대상입니다.
              </li>
              <li>
                이벤트 참여시, 이메일, 문자, 야간 카카오톡 수신에 모두
                동의됩니다.
              </li>
              <li>
                이미 광고성 수신 동의를 한 고객분들은 이벤트 당첨에서
                제외됩니다.
              </li>
              <li>
                해당 마케팅 동의를 거부하실 수 있으나, 이 경우 본 행사에 참여는
                불가능합니다.
              </li>
              <li>
                개인정보 설정 변경으로 인해 수신 동의가 해제된 경우, 참여에서
                제외될 수 있습니다.{' '}
              </li>
              <li>1개의 계정당 한 번만 이벤트 참여가 가능합니다.</li>
              <li>
                당첨 상품은 입력된 휴대폰 번호로 발송되며, 오류 입력 시 당첨
                대상에서 제외됩니다.
              </li>
              <li>
                추첨을 통해 당첨되는 당첨자는 8/28(목) 쪽지로 개별 안내 드릴
                예정입니다.
              </li>
              <li>
                이벤트 당첨자는 쪽지로 개별 안내 예정이오니 사전에 쪽지 수신
                거부를 해제해 주세요.
              </li>
              <li>
                당첨자 발표 및 상품 지급일은 당사 사정에 의해 변경될 수
                있습니다.
              </li>
            </ul>
          </div>
        </div>

        <button
          type='button'
          className={`${styles.btn_agree} ${INACTIVE_BUTTON_TEXT[buttonStatus] ? styles.disabled : ''}`}
          disabled={INACTIVE_BUTTON_TEXT[buttonStatus]}
          onClick={handleJoinEvent}>
          <span>
            {INACTIVE_BUTTON_TEXT[buttonStatus] || '마케팅 수신 동의하기'}
          </span>
        </button>

        {layerStatus && (
          <div className={styles.layer_modal_wrap}>
            <div className={styles.flex_box}>
              <div className={styles.alert_layer}>
                <div className={styles.contents}>
                  {layerStatus === 1 ? (
                    <div className={styles.success}>
                      <strong className={styles.title}>
                        이벤트 참여가 <em>완료되었습니다.</em>
                      </strong>
                      <p className={styles.desc}>
                        <strong>이메일/SMS 수신</strong>에 동의하셨습니다.
                        <br />
                        이벤트 당첨자는 추첨을 통해 선정되며, <br />
                        쪽지로 개별 안내 드릴 예정입니다.
                      </p>
                      <div className={styles.noti_box}>
                        <p>
                          마케팅 수신에 동의하신 고객님에 한해,
                          <br />
                          [새로워진 별풍선 구매안내]를 알림톡으로 전달드립니다.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className={styles.msg}>
                      {LAYER_MESSAGE[layerStatus] ||
                        '잠시 후\n다시 시도해주세요.'}
                    </p>
                  )}
                </div>
                <div className={styles.btn_box}>
                  <button type='button' onClick={closeLayer}>
                    확인
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
};

export default MarketingConsent;
